import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Button,
  Heading,
  Spacer,
  Image,
  HStack,
  Badge,
  Link as ChakraLink,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import axios from 'axios';
import { ChevronDownIcon, SearchIcon } from '@chakra-ui/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import FeatureUnlockContext from '../context/FeatureUnlockContext';
import finanseeLogo from '../assets/finanseeLogo.png';
import { buildSyntheticWhoToCall, demoCustomers, getCustomerDisplayName } from '../content/demoAnalytics';
import { buildPurchaseIntentUrl, normalizePurchaseIntent } from '../utils/purchaseIntent';
import { useTranslation } from '../i18n';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
  ? process.env.REACT_APP_API_BASE_URL_PROD
  : process.env.REACT_APP_API_BASE_URL_DEV;

const staticSearchItems = [
  { label: 'Platform impact', description: 'Admin impact, RM workflow, conversion', path: '/dashboard/admin?view=impact', roles: ['admin'] },
  { label: 'Who to call', description: 'Relationship Manager priority queue', path: '/dashboard/rm?view=who-to-call', roles: ['admin', 'rm'] },
  { label: 'Head adoption signals', description: 'Team adoption and coaching actions', path: '/dashboard/head?view=adoption', roles: ['admin', 'head'] },
  { label: 'Portfolio drift', description: 'Allocation drift and mismatch severity', path: '/dashboard/portfolio?view=drift', roles: ['admin', 'portfolio_adviser'] },
  { label: 'Compliance evidence', description: 'SLA aging and missing evidence', path: '/dashboard/compliance?view=evidence', roles: ['admin', 'compliance'] },
  { label: 'Reports and exports', description: 'PDF / CSV report packs', path: '/reportsExports', roles: ['admin', 'rm', 'compliance'] },
  { label: 'Datasets', description: 'Uploaded customer templates and processing status', path: '/datasets', roles: ['admin'] },
];

const roleMatches = (item, user) => {
  if (!user?.role) return false;
  if (item.roles?.includes(user.role)) return true;
  return user.role === 'portfolio_adviser' && item.roles?.includes('portfolio');
};

const normalizeCustomerResult = (customer, targetPath) => ({
  label: getCustomerDisplayName(customer),
  description: `${customer.customer_id || 'Customer'} · ${customer.country_iso || 'n/a'} · Risk ${customer.risk_profile ?? 'n/a'}`,
  path: targetPath,
  type: 'Customer',
});

const PRE_LOGIN_ROUTES = new Set(['/', '/demo', '/login', '/register', '/platform', '/reset-password', '/verify-otp']);

function WorkspaceSearch({ user, token }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  const staticResults = useMemo(() => (
    staticSearchItems
      .filter((item) => roleMatches(item, user))
      .filter((item) => {
        const haystack = `${item.label} ${item.description}`.toLowerCase();
        return !query.trim() || haystack.includes(query.trim().toLowerCase());
      })
      .map((item) => ({ ...item, type: 'Workspace' }))
      .slice(0, 5)
  ), [query, user]);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    const currentQuery = query.trim();
    if (!token || currentQuery.length < 2) {
      setResults([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timeout = setTimeout(async () => {
      setLoading(true);
      try {
        let customerResults = [];

        if (user?.role === 'rm') {
          const response = await axios.get(`${API_BASE_URL}/content/rm-who-to-call`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const whoToCall = Array.isArray(response.data) ? response.data : [];
          customerResults = whoToCall
            .filter((customer) => `${customer.customer_id} ${customer.country_iso} ${getCustomerDisplayName(customer)}`.toLowerCase().includes(currentQuery.toLowerCase()))
            .slice(0, 6)
            .map((customer) => normalizeCustomerResult(customer, `/dashboard/rm?view=who-to-call&search=${encodeURIComponent(customer.customer_id || currentQuery)}`));
        } else if (['admin', 'compliance'].includes(user?.role)) {
          const response = await axios.get(`${API_BASE_URL}/content/list-customers`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { page: 1, page_size: 8, search: currentQuery },
          });
          customerResults = (response.data?.customers || [])
            .map((customer) => normalizeCustomerResult(customer, `/dashboard/admin?view=scores&search=${encodeURIComponent(customer.customer_id || currentQuery)}`));
        }

        if (!cancelled) setResults(customerResults);
      } catch (error) {
        const fallback = buildSyntheticWhoToCall(demoCustomers)
          .filter((customer) => `${customer.customer_id} ${customer.country_iso} ${customer.segment}`.toLowerCase().includes(currentQuery.toLowerCase()))
          .slice(0, 6)
          .map((customer) => normalizeCustomerResult(customer, `/dashboard/rm?view=who-to-call&search=${encodeURIComponent(customer.customer_id)}`));

        if (!cancelled) setResults(fallback);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [query, token, user]);

  const visibleResults = [...results, ...staticResults].slice(0, 8);

  const openResult = (path) => {
    setIsOpen(false);
    setQuery('');
    navigate(path);
  };

  const submitSearch = () => {
    const firstResult = visibleResults[0];
    if (firstResult) {
      openResult(firstResult.path);
      return;
    }

    if (query.trim()) {
      navigate(`/dashboard/admin?view=scores&search=${encodeURIComponent(query.trim())}`);
      setIsOpen(false);
    }
  };

  return (
    <Box
      ref={wrapperRef}
      display={{ base: 'none', lg: 'block' }}
      maxW="430px"
      w="full"
      position="relative"
    >
      <InputGroup>
        <InputLeftElement pointerEvents="none">
          {loading ? <Spinner size="xs" color="whiteAlpha.700" /> : <SearchIcon color="whiteAlpha.700" boxSize={3.5} />}
        </InputLeftElement>
        <Input
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submitSearch();
            if (event.key === 'Escape') setIsOpen(false);
          }}
          placeholder="Search customers, KPIs, files..."
          bg="whiteAlpha.100"
          color="white"
          borderColor="whiteAlpha.200"
          _placeholder={{ color: 'whiteAlpha.700' }}
          _hover={{ borderColor: 'whiteAlpha.300' }}
          _focus={{ borderColor: 'brand.teal', boxShadow: '0 0 0 1px var(--chakra-colors-brand-teal)' }}
          borderRadius="8px"
          h="38px"
          fontSize="13px"
        />
      </InputGroup>

      {isOpen ? (
        <Box
          position="absolute"
          top="44px"
          left={0}
          right={0}
          bg="white"
          color="brand.ink"
          borderRadius="12px"
          boxShadow="0 18px 42px rgba(11, 37, 69, 0.22)"
          borderWidth="1px"
          borderColor="brand.divider"
          overflow="hidden"
          zIndex={1400}
        >
          <VStack align="stretch" spacing={0}>
            {visibleResults.length > 0 ? visibleResults.map((result, index) => (
              <Button
                key={`${result.type}-${result.label}-${index}`}
                variant="ghost"
                justifyContent="flex-start"
                h="auto"
                minH="58px"
                borderRadius={0}
                py={3}
                px={4}
                onClick={() => openResult(result.path)}
              >
                <Box textAlign="left" minW={0}>
                  <HStack spacing={2}>
                    <Badge colorScheme={result.type === 'Customer' ? 'teal' : 'blue'}>{result.type}</Badge>
                    <Text fontWeight="800" isTruncated>{result.label}</Text>
                  </HStack>
                  <Text mt={1} fontSize="12px" color="brand.subtle" isTruncated>{result.description}</Text>
                </Box>
              </Button>
            )) : (
              <Box px={4} py={4}>
                <Text fontSize="13px" fontWeight="800">No matching result yet</Text>
                <Text mt={1} fontSize="12px" color="brand.subtle">Try a customer ID such as CUST3490, or search for reports, bias, dataset, or compliance.</Text>
              </Box>
            )}
          </VStack>
        </Box>
      ) : null}
    </Box>
  );
}

const Navbar = () => {
  const { t } = useTranslation();
  const { token, user, logout } = useContext(AuthContext);
  const { resetUnlocks } = useContext(FeatureUnlockContext);
  const navigate = useNavigate();
  const location = useLocation();
  const purchaseIntent = useMemo(
    () => normalizePurchaseIntent(location.search),
    [location.search]
  );
  const platformHref = buildPurchaseIntentUrl('/platform', purchaseIntent);
  const loginHref = buildPurchaseIntentUrl('/login', purchaseIntent);
  const isPreLogin = PRE_LOGIN_ROUTES.has(location.pathname);
  const surface = isPreLogin ? 'brand.quantumBg' : 'brand.navy';
  const border = isPreLogin ? 'brand.quantumBorder' : 'whiteAlpha.200';
  const linkHover = isPreLogin ? 'brand.quantumAccent' : 'brand.divider';
  const accountName = user?.businessName || user?.business_name || user?.companyName || user?.name || user?.email || 'Account';
  const roleLabel = {
    admin: 'Admin',
    rm: 'Relationship Manager',
    head: 'Head of Advisory',
    portfolio_adviser: 'Portfolio Adviser',
    compliance: 'Compliance',
    view_only: 'View Only',
  }[user?.role] || 'Workspace';
  const initials = (accountName || 'FS')
    .split(/\s|@/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'FS';

  const handleLogout = () => {
    resetUnlocks();
    logout();
    navigate('/');
  };

  return (
    <Box
      bg={surface}
      px={{ base: 4, md: 6 }}
      py={0}
      color="white"
      borderBottom="1px solid"
      borderColor={border}
      w="full"
      minH="56px"
      position="sticky"
      top={0}
      zIndex={1100}
      backdropFilter={isPreLogin ? 'blur(12px)' : undefined}
    >
      <Flex align="center" minH="56px" gap={4}>
        <Flex align="center" gap={3} minW={{ base: 'auto', lg: '260px' }}>
          <Box
            w="32px"
            h="32px"
            borderRadius="8px"
            bg="white"
            overflow="hidden"
            display="grid"
            placeItems="center"
            flexShrink={0}
          >
            <Image
              src={finanseeLogo}
              alt="FinanSee Logo"
              boxSize="32px"
              objectFit="contain"
            />
          </Box>
          <Box lineHeight="1.05">
          <Heading
            as={Link}
            to="/"
            fontSize="15px"
            fontWeight="800"
            color="white"
            cursor="pointer"
            textShadow={isPreLogin ? '0 0 22px rgba(34, 211, 238, 0.28)' : undefined}
            _hover={{ textDecoration: 'none', color: 'white' }}
          >
            FinanSee
          </Heading>
            <Text
              display={{ base: 'none', md: 'block' }}
              mt="3px"
              fontSize="10.5px"
              color="whiteAlpha.700"
              fontFamily="mono"
            >
              playground.finansee.eu
            </Text>
          </Box>
        </Flex>
        {token ? <WorkspaceSearch user={user} token={token} /> : null}
        <Spacer />
        {token ? (
          <HStack spacing={3}>
            <Badge
              display={{ base: 'none', md: 'inline-flex' }}
              bg="rgba(94, 234, 212, 0.18)"
              color="#5eead4"
              px={3}
              py={1}
            >
              {roleLabel}
            </Badge>
          <Menu placement="bottom-end">
            <MenuButton
              as={Button}
              rightIcon={<ChevronDownIcon />}
                color="brand.navy"
                bg="white"
              size="md"
                px={3}
                h="38px"
                maxW={{ base: '190px', md: '300px' }}
              _hover={{
                  bg: 'brand.tealSoft',
                transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(11, 37, 69, 0.18)'
              }}
              _active={{
                  bg: 'gray.100',
                transform: 'translateY(0)'
              }}
              transition="all 0.2s"
              fontWeight="semibold"
              boxShadow="md"
            >
                <HStack spacing={2} minW={0}>
                  <Box
                    w="24px"
                    h="24px"
                    borderRadius="full"
                    bg="brand.teal"
                    color="brand.navy"
                    display="grid"
                    placeItems="center"
                    fontSize="10px"
                    fontWeight="800"
                    flexShrink={0}
                  >
                    {initials}
                  </Box>
                  <Text as="span" isTruncated display="block">
                    {accountName}
                  </Text>
                </HStack>
            </MenuButton>
            <MenuList color="gray.800" minW="240px">
              {user?.role === 'admin' ? (
                <>
                  <MenuItem onClick={() => navigate('/settings')}>Settings</MenuItem>
                  <MenuItem onClick={() => navigate('/subscription')}>Subscription</MenuItem>
                </>
              ) : null}
              {['admin', 'compliance'].includes(user?.role) ? (
                <MenuItem onClick={() => navigate('/auditsCompliance')}>Audits</MenuItem>
              ) : null}
              {['admin', 'rm'].includes(user?.role) ? (
                <MenuItem onClick={() => navigate('/reportsExports')}>Reports</MenuItem>
              ) : null}
              <MenuDivider />
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </MenuList>
          </Menu>
          </HStack>
        ) : (
          <HStack spacing={4}>
            <ChakraLink
              as={Link}
              to={platformHref}
              display={{ base: 'none', md: 'inline-flex' }}
              fontWeight="semibold"
              color="white"
              _hover={{ textDecoration: 'none', color: linkHover }}
            >
              {t('nav.plans')}
            </ChakraLink>
            <Button
              as={Link}
              to={loginHref}
              color={isPreLogin ? 'brand.quantumBg' : 'brand.navy'}
              bg={isPreLogin ? undefined : 'white'}
              bgGradient={isPreLogin ? 'linear(135deg, brand.quantumAccent, brand.quantumCyan)' : undefined}
              size="md"
              px={{ base: 4, md: 6 }}
              _hover={{
                bg: isPreLogin ? undefined : 'brand.tealSoft',
                opacity: isPreLogin ? 0.9 : 1,
                transform: 'translateY(-1px)',
                boxShadow: isPreLogin ? '0 10px 26px rgba(0, 212, 170, 0.22)' : '0 4px 12px rgba(11, 37, 69, 0.18)'
              }}
              _active={{
                bg: isPreLogin ? undefined : 'gray.200',
                transform: 'translateY(0)'
              }}
              transition="all 0.2s"
              fontWeight="semibold"
              boxShadow="md"
            >
              {t('nav.login')}
            </Button>
          </HStack>
        )}
      </Flex>
    </Box>
  );
};

export default Navbar;
